import { HttpErrorResponse } from '@angular/common/http'
import {
  PackingList,
  PackingListAuthData,
  PackingListsListingData,
  PackingListsListingPage,
  PackingListStationData,
} from '../models/packing-list.entity'
import { computed, Injectable, signal } from '@angular/core'
import { PackingListsRepository } from '../packing-lists.repository'
import { isQueryStringFiltered } from '../../../libs/query-string.lib'
import {
  PackingListField,
  PackingListSearchParams,
} from '../models/packing-list-search.model'
import { mergeById } from '../../../libs/filter.lib'
import { Observable, of } from 'rxjs'
import { WarehousesService } from '../../warehouses'
import { UsersService } from '../../users'
import {
  PICKING_LIST_TYPES,
  PickingList,
  PickingListsService,
  PickingListStatus,
} from '../../picking-lists'
import { TranslationService } from '@evologi/shared/util-translation'
import { Location } from '../../locations'
import { PrintingType } from '../models/packing-list.types'

interface PackingListsListingState {
  data?: PackingList[]
  extData?: PackingListsListingData
  dropdownOptions?: PackingListsListingData
  totalCount?: number
  stationData?: PackingListStationData
  activePickingLists?: PickingList[]
  fields?: PackingListField[]
  filters?: PackingListSearchParams
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const PACKING_LISTS_INITIAL_STATE: PackingListsListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class PackingListsListingStore {
  constructor(
    private packingListsRepository: PackingListsRepository,
    private warehousesService: WarehousesService,
    private usersService: UsersService,
    private pickingListsService: PickingListsService,
    private translationService: TranslationService,
  ) {}

  private state = signal<PackingListsListingState>(PACKING_LISTS_INITIAL_STATE)

  // Selectors
  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return isQueryStringFiltered(filters ?? {})
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly packingLists = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly extData = computed(() => this.state().extData)
  readonly fields = computed(() => this.state().fields)
  readonly pickingLists = computed(() => this.extData()?.pickingLists || [])
  readonly activePickingLists = computed(() => this.state().activePickingLists)
  readonly warehouses = computed(() => this.extData()?.warehouses || [])
  readonly users = computed(() => this.extData()?.users || [])
  readonly stationData = computed(() => {
    const stationData = this.state().stationData
    if (!stationData?.warehouseId || !stationData.printingType) {
      return undefined
    }
    return stationData
  })
  readonly printers = computed(() => {
    const stationData = this.stationData()
    return {
      labels: stationData?.printerId,
      a4: stationData?.A4PrinterId,
    }
  })
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly warehouseId = computed(() => this.stationData()?.warehouseId)

  // Effects

  async search(
    params?: PackingListSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .searchPackingLists$(
          this.parseStateFilterParams(this.state(), params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async initStationData(authData: PackingListAuthData): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository.loadPackingStationData$(authData).subscribe({
        next: async (data) => {
          const pickingLists = await this.getPickingLists(data)
          this.setStationData(data, pickingLists)
          resolve()
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  async getPickingLists(
    data: PackingListStationData,
  ): Promise<PickingList[] | undefined> {
    if (!data.warehouseId) {
      return undefined
    }

    return new Promise((resolve) => {
      this.pickingListsService
        .search$({
          status: [PickingListStatus.processing, PickingListStatus.picked],
          warehouseId: data.warehouseId,
        })
        .subscribe({
          next: (data) => {
            resolve(data.data)
          },
          error: (error) => {
            resolve(undefined)
            this.setError(error)
          },
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof PackingListsListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<PackingListsListingData[LookupKey]>
    >

    const queryKey = `${event.dataDisplayField ?? 'name'}:ct`
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      types: () =>
        of(
          PICKING_LIST_TYPES.map((item) => ({
            ...item,
            name: this.translationService.translate(item.name),
          })),
        ),
      warehouses: () => this.warehousesService.list$(query),
      users: () => this.usersService.list$(query),
      pickingLists: () => this.pickingListsService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  saveStationData(stationData: PackingListStationData) {
    this.packingListsRepository.savePackingStationData(stationData)
    this.setStationData(stationData)
  }

  // Reducers

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  setFields(fields?: PackingListField[]) {
    this.state.set({ ...this.state(), fields })
  }

  setFilters(filters?: PackingListSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setStationData(
    stationData: PackingListStationData,
    activePickingLists?: PickingList[],
  ) {
    this.state.set({
      ...this.state(),
      isLoading: false,
      stationData,
      activePickingLists:
        activePickingLists !== undefined
          ? activePickingLists
          : this.activePickingLists(),
    })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  setData(data: PackingListsListingPage) {
    const extData = this.extData()
    const newExtData = {
      users: mergeById(extData?.users, data.extData?.users),
      warehouses: mergeById(extData?.warehouses, data.extData?.warehouses),
      pickingLists: mergeById(
        extData?.pickingLists,
        data.extData?.pickingLists,
      ),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  async updateStationData(data: {
    warehouseId?: string
    computerId?: string
    printerId?: string
    A4PrinterId?: string
    packagingLocation?: Location
    printingType?: string
  }) {
    const stationData = this.stationData()
    if (!stationData) {
      return
    }

    let activePickingLists = this.activePickingLists()

    if (data.warehouseId) {
      stationData.warehouseId = data.warehouseId
      this.setIsLoading(true)
      activePickingLists = await this.getPickingLists(stationData)
    }
    if (data.computerId) {
      stationData.computerId = data.computerId
    }
    if (data.printerId) {
      stationData.printerId = data.printerId
    }
    if (data.A4PrinterId) {
      stationData.A4PrinterId = data.A4PrinterId
    }
    if ('packagingLocation' in data) {
      stationData.packagingLocation = data.packagingLocation
    }
    if (
      stationData.packagingLocation &&
      stationData.packagingLocation.warehouseId !== stationData.warehouseId
    ) {
      stationData.packagingLocation = undefined
    }
    if (data.printingType) {
      stationData.printingType = data.printingType as PrintingType
    }

    this.state.set({
      ...this.state(),
      activePickingLists,
      stationData: { ...stationData },
      isLoading: false,
    })
    this.saveStationData(stationData)
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  /**
   * Utilities
   */

  private parseStateFilterParams(
    state: PackingListsListingState,
    filters?: PackingListSearchParams,
  ): PackingListSearchParams {
    const params = filters ? { ...filters } : {}

    if (!params.status || params.status.length < 1) {
      params.status = ['PROCESSING', 'PACKED']
    }

    if (!params.warehouseId && state.stationData?.warehouseId) {
      params.warehouseId = state.stationData.warehouseId
    }

    return params
  }
}
